
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  Keyboard,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import { Svg, Path } from 'react-native-svg';
import OnboardingLayout from '@/components/OnboardingLayout';
import { useOnboardingStepTracking } from '@/hooks/useOnboardingStepTracking';
import CircularArrowButton from '@/components/CircularArrowButton';
import { TempOnboardingService } from '@/services/tempOnboardingService';

const ADDICTION_OPTIONS = [
  'alcohol',
  'nicotine',
  'drugs',
  'gambling',
  'sex',
  'pornography',
  'other',
];

export default function OnboardingAddictionTypeScreen() {
  const { logCompleted } = useOnboardingStepTracking('addiction_type');
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Keyboard.dismiss();
    const loadSavedData = async () => {
      const data = await TempOnboardingService.getData();
      if (data.addictionTypes) {
        setSelectedTypes(data.addictionTypes);
        console.log('📥 Loaded previously saved addiction types:', data.addictionTypes);
      }
    };
    loadSavedData();
  }, []);

  const getOptionLabel = (option: string): string => {
    const labels: Record<string, string> = {
      alcohol: t('onboarding.addictionType.options.alcohol'),
      nicotine: t('onboarding.addictionType.options.nicotine'),
      drugs: t('onboarding.addictionType.options.drugs'),
      gambling: t('onboarding.addictionType.options.gambling'),
      sex: t('onboarding.addictionType.options.sex'),
      pornography: t('onboarding.addictionType.options.pornography'),
      other: t('onboarding.addictionType.options.other'),
    };
    return labels[option] || option;
  };

  const handleTypeToggle = (type: string) => {
    setSelectedTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(item => item !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const handleContinue = async () => {
    if (selectedTypes.length === 0) {
      Alert.alert(
        t('onboarding.addictionType.required'),
        t('onboarding.addictionType.requiredMessage')
      );
      return;
    }

    setLoading(true);
    try {
      await TempOnboardingService.saveAddictionTypes(selectedTypes);
      console.log('✅ Addiction types saved:', selectedTypes);
      // PRD §5.2 — `onboarding_step_completed` for the `addiction_type` step.
      logCompleted();
      router.push('/onboarding-frequency');
    } catch (error) {
      console.error('❌ Error saving addiction types:', error);
      Alert.alert(
        t('onboarding.addictionType.error'),
        t('onboarding.addictionType.errorMessage')
      );
    } finally {
      setLoading(false);
    }
  };

  const isSelected = (type: string) => selectedTypes.includes(type);

  const progressWidth = 64; // Step 6 of 8

  return (
    <OnboardingLayout progressWidth={progressWidth}>
      <View style={styles.contentContainer}>
        <ScrollView 
          style={styles.scrollView} 
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.section}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepNumber}>6</Text>
            </View>

            <View style={styles.textContainer}>
              <Text style={styles.title}>{t('onboarding.addictionType.title')}</Text>
              <Text style={styles.subtitle}>
                {t('onboarding.addictionType.subtitle')}
              </Text>
            </View>
          </View>

          <View style={styles.optionsSection}>
            <View style={styles.cardsContainer}>
              {ADDICTION_OPTIONS.map((type) => (
                <TouchableOpacity
                  key={type}
                  style={[
                    styles.card,
                    isSelected(type) && styles.cardSelected
                  ]}
                  onPress={() => handleTypeToggle(type)}
                  activeOpacity={0.7}
                >
                  <Text style={[
                    styles.cardText,
                    isSelected(type) && styles.cardTextSelected
                  ]}>
                    {getOptionLabel(type)}
                  </Text>
                  <View style={[
                    styles.checkbox,
                    isSelected(type) && styles.checkboxSelected
                  ]}>
                    {isSelected(type) && (
                      <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <Path 
                          d="M12.6673 4.7915L6.25065 11.2082L3.33398 8.2915" 
                          stroke="#F0EEE4" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                        />
                      </Svg>
                    )}
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </ScrollView>

        <View style={[styles.buttonContainer, { bottom: insets.bottom + 24 }]}>
          <CircularArrowButton
            onPress={handleContinue}
            disabled={loading || selectedTypes.length === 0}
          />
        </View>
      </View>
    </OnboardingLayout>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: 100,
  },
  section: {
    padding: 24,
    paddingBottom: 0,
    alignItems: 'flex-start',
    gap: 24,
  },
  stepBadge: {
    backgroundColor: '#151515',
    borderRadius: 32,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumber: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    lineHeight: 28,
  },
  textContainer: {
    alignItems: 'flex-start',
    gap: 8,
  },
  title: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    width: 345,
  },
  subtitle: {
    color: '#56554D',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
    width: 345,
  },
  optionsSection: {
    padding: 24,
  },
  cardsContainer: {
    gap: 8,
  },
  card: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#FFFFFF',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    height: 64,
  },
  cardSelected: {
    borderColor: '#004165',
    backgroundColor: '#E6EBF0',
  },
  cardText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    flex: 1,
    lineHeight: 19.6,
  },
  cardTextSelected: {
    color: '#151515',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxSelected: {
    borderWidth: 3,
    borderColor: '#015480',
    backgroundColor: '#015480',
  },
  buttonContainer: {
    position: 'absolute',
    right: 24,
  },
});
